import { createSelector } from 'reselect';

import { utils } from '../../../utils';
import { deliveryKey } from '../../../utils/makeUtils';
import { getRouteProps } from '../../../weborder/redux/basket/selectors';
import {
  GetCollectionProps,
  GetDeliveryProps,
  GetRestaurantProps,
} from './store.types.d';

export const getRestaurantDetailsWrapper = (state: WebOrder.State) => {
  const props = getRouteProps(state);

  if (!props) {
    return undefined;
  }

  return getRestaurantDetails(state, props);
};

const getRestaurantDetails = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) =>
  props.deliveryType === 'collection'
    ? getCollectionDetails(state, props)
    : getDeliveryDetails(state, props);

const getCollectionDetails = createSelector(
  [
    (_state: WebOrder.State, props: GetCollectionProps) =>
      utils.change.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetCollectionProps) =>
      utils.change.stringToNumber(props.prId),
    (state: WebOrder.State) => state.collectionRestaurants,
  ],
  (vrId, prId, collectionDetails) => {
    if (!vrId || !prId) {
      return undefined;
    }

    const details = collectionDetails[prId];

    if (details && details.status === 'success') {
      return details.data;
    }

    return undefined;
  },
);

const getDeliveryDetails = createSelector(
  [
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      utils.change.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      utils.change.stringToNumber(props.deliveryLocationId),
    (state: WebOrder.State) => state.deliveryRestaurants,
  ],
  (vrId, deliveryLocationId, deliveryRestaurants) => {
    if (!vrId || !deliveryLocationId) {
      return undefined;
    }

    const key = deliveryKey(vrId, deliveryLocationId);

    const details = deliveryRestaurants[key];
    if (details && details.status === 'success') {
      return details.data;
    }

    return undefined;
  },
);

const getRestaurantSummary = (
  state: WebOrder.State,
  props: GetRestaurantProps,
) =>
  props.deliveryType === 'collection'
    ? getCollectionSummary(state, props)
    : getDeliverySummary(state, props);

const getCollectionSummary = createSelector(
  [
    (_state: WebOrder.State, props: GetCollectionProps) =>
      utils.change.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetCollectionProps) =>
      utils.change.stringToNumber(props.prId),
    (state: WebOrder.State) => state.flipdish.CollectionSummaries,
  ],
  (vrId, prId, CollectionSummaries) => {
    if (!vrId || !prId) {
      return null;
    }

    if (CollectionSummaries && CollectionSummaries.status === 'success') {
      const config = CollectionSummaries.data.find(
        (s) =>
          s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId,
      );

      return config;
    }

    return null;
  },
);

const getDeliverySummary = createSelector(
  [
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      utils.change.stringToNumber(props.vrId),
    (_state: WebOrder.State, props: GetDeliveryProps) =>
      utils.change.stringToNumber(props.deliveryLocationId),
    (state: WebOrder.State) => state.deliveryOptions,
  ],
  (vrId, deliveryLocationId, deliveryOptions) => {
    if (!vrId || !deliveryLocationId) {
      return null;
    }

    const deliveryOption = deliveryOptions[deliveryLocationId];
    if (deliveryOption && deliveryOption.status === 'success') {
      if (deliveryOption.data.type === 'skip') {
        return deliveryOption.data.to;
      }

      return deliveryOption.data.of.find((o) => o.VirtualRestaurantId === vrId);
    }

    return null;
  },
);

export const getLoyaltyCampaignsForStore = createSelector(
  [
    (state: WebOrder.State, _storeId: number) => state.store?.loyaltyCampaigns,
    (_state: WebOrder.State, storeId: number) => storeId,
  ],
  (loyaltyCampaigns, storeId) => {
    if (loyaltyCampaigns) {
      const loyaltyCampaignsForStore = loyaltyCampaigns[storeId];

      if (loyaltyCampaignsForStore?.status === 'success') {
        return loyaltyCampaignsForStore.data;
      }
    }

    return [];
  },
);

export const getActiveLoyaltyCampaignForStore = createSelector(
  [
    (state: WebOrder.State, storeId: number) =>
      getLoyaltyCampaignsForStore(state, storeId),
  ],
  (loyaltyCampaignsForStore) => {
    if (loyaltyCampaignsForStore?.length > 0) {
      return loyaltyCampaignsForStore[loyaltyCampaignsForStore.length - 1];
    }
    return undefined;
  },
);

export const getVouchersForStore = createSelector(
  [
    (state: WebOrder.State, storeId: number) =>
      getLoyaltyCampaignsForStore(state, storeId),
  ],
  (loyaltyCampaignsForStore) => {
    if (loyaltyCampaignsForStore?.length) {
      const vouchersForStore = [];
      loyaltyCampaignsForStore.forEach((campaign) => {
        if (campaign.Rewards) {
          const isDiscountPercentage =
            campaign.Rewards[0].Type === 'TotalOrderAmountDiscountPercentage';
          vouchersForStore.push({
            code: campaign.Voucher.Code,
            description: campaign.Description,
            discountValue: isDiscountPercentage
              ? `${campaign.Rewards[0].Value}%`
              : campaign.Rewards[0].Value,
            expiryDate: campaign.Voucher.ExpireDate,
            type: campaign.Rewards[0].Type,
          });
        }
      });
      return vouchersForStore;
    }

    return [];
  },
);

export const storeSelectors = {
  getCollectionDetails,
  getCollectionSummary,
  getDeliveryDetails,
  getDeliverySummary,
  getRestaurantDetails,
  getRestaurantDetailsWrapper,
  getRestaurantSummary,
};
